<template>
  <div
    class="flex-row-union"
    style="align-items: flex-start;"
  >
    <PrmSkeleton v-if="licenseStore.isLoadingLicense" />
    <TextCopy
      v-else-if="mode === EFormMode.READ"
      style="flex: 1 1 auto;"
      monospaced
      :copy-string="JSON.stringify(licenseStore.license, null, 2)"
    />
    <InputTextArea
      v-else
      v-model="licenseKey"
      style="flex-grow: 1;"
      :disabled="isSubmitting"
      :invalid="!isLicenseValid"
      monospaced
      :error-message="isLicenseValid ? undefined : i18n.global.t(`api.QmulusLicenseViolationReason.${licenseViolation}`)"
      :rows="10"
    />
    <div
      class="flex-column"
      style="flex-grow: 0;"
    >
      <SemanticButton
        v-if="mode !== EFormMode.READ"
        variant="save"
        :disabled="!licenseKey"
        :loading="isSubmitting"
        @click="setLicenseKey"
      />
      <SemanticButton
        v-if="mode === EFormMode.EDIT"
        variant="cancel"
        :disabled="isSubmitting"
        @click="onReset"
      />
      <SemanticButton
        v-if="mode === EFormMode.READ"
        variant="edit"
        @click="emit('update:mode', EFormMode.EDIT)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import PrmSkeleton from '@NitrexGroup/primevue/skeleton';
import {
  i18n,
} from '@i18n';
import SemanticButton from '@qmulus/ui/components/button/semantic-button.vue';
import InputTextArea from '@qmulus/ui/components/input-textarea/input-textarea.vue';
import TextCopy from '@qmulus/ui/components/text-copy/text-copy.vue';
import {
  EFormMode,
} from '@qmulus/ui/types/form.ts';
import {
  shallowRef,
} from 'vue';

import {
  useLicenseStore,
} from '@/store/pinia/license.ts';
import {
  TComponentValue,
} from '@/types/openapi-type.ts';

defineProps<{
  mode: EFormMode,
}>();

const emit = defineEmits<{
  (e: 'update:mode', value: EFormMode): void,
  (e: 'onLicenseKeyUpdate'): void,
}>();

const licenseKey = shallowRef<string>('');
const isSubmitting = shallowRef<boolean>(false);

const isLicenseValid = shallowRef<boolean>(true);
const licenseViolation = shallowRef<TComponentValue<'QmulusLicenseViolationReason'> | null>(null);

const licenseStore = useLicenseStore();
licenseStore.loadLicense();

function setLicenseKey() {
  isSubmitting.value = true;
  licenseStore.setLicense(licenseKey.value)
    .then((response) => {
      isLicenseValid.value = response.data.isValid;
      licenseViolation.value = response.data.violation;
      if (response.data.isValid) {
        licenseStore.loadLicense(true)
          .then(() => {
            emit('update:mode', EFormMode.READ);
          });
        emit('onLicenseKeyUpdate');
      }
      return response;
    }).finally(() => {
      isSubmitting.value = false;
    });
}

function onReset() {
  licenseKey.value = '';
  emit('update:mode', EFormMode.READ);
}
</script>
